import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle, useMetaDescription, useSocialMeta } from '@/hooks/usePageTitle';
import { PublicShell } from '@/components/visual/PublicShell';
import { LocaleLink } from '@/components/LocaleLink';
import { useLocale } from '@/hooks/useLocale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Shield, ArrowRight, ArrowLeft, CheckCircle2, Lock, Loader2,
  Sparkles, Calculator, BrainCircuit, Zap
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import logger from '@/lib/logger';

const API = '/api';
const QUESTION_IDS = ['m1', 'm2', 'm3', 'm4', 'm5', 'm6'];
const OPTIONS = [
  { value: 0, key: 'no' },
  { value: 1, key: 'progress' },
  { value: 2, key: 'yes' },
];
const TIER_COLOR = {
  early: { ring: '#B42318', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  developing: { ring: '#B7791F', bg: 'bg-[#B7791F]/[0.06]', text: 'text-[#B7791F]', border: 'border-[#B7791F]/25' },
  advanced: { ring: '#78B928', bg: 'bg-[#F1F8E8]', text: 'text-[#527F19]', border: 'border-[#527F19]/25' },
};
const EMAIL_RE = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

function ScoreRing({ score, color }) {
  const r = 54;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <div className="relative w-40 h-40" data-testid="mini-score-ring">
      <svg viewBox="0 0 128 128" className="w-40 h-40 -rotate-90">
        <circle cx="64" cy="64" r={r} fill="none" stroke="#DDE1E5" strokeWidth="10" />
        <circle
          cx="64" cy="64" r={r} fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-semibold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="mini-score-value">
          {score}
        </span>
        <span className="text-xs text-[#8A929B]">/ 100</span>
      </div>
    </div>
  );
}

export default function MiniAssessmentPage() {
  const { t } = useTranslation();
  const { lang, localePath } = useLocale();
  usePageTitle(t('miniAssessment.meta.title'));
  useMetaDescription(t('miniAssessment.meta.description'));
  useSocialMeta({
    title: t('miniAssessment.meta.title'),
    description: t('miniAssessment.meta.description'),
    url: `https://ismscalculator.com/${lang}/mini-assessment`,
  });

  const [phase, setPhase] = useState('questions'); // questions | gate | result
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [form, setForm] = useState({ email: '', name: '', company: '', consent: false });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const total = QUESTION_IDS.length;
  const currentId = QUESTION_IDS[step];
  const answered = answers[currentId] !== undefined;
  let progressValue = Math.round((step / total) * 100);
  if (phase === 'gate') progressValue = 100;
  if (phase === 'result') progressValue = 100;

  const setAnswer = (val) => setAnswers((prev) => ({ ...prev, [currentId]: val }));

  const goNext = () => {
    if (step < total - 1) setStep(step + 1);
    else setPhase('gate');
  };
  const goBack = () => {
    if (phase === 'gate') { setPhase('questions'); return; }
    if (step > 0) setStep(step - 1);
  };

  const handleReveal = async (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(form.email.trim())) {
      toast.error(t('miniAssessment.errors.invalidEmail'));
      return;
    }
    if (!form.consent) {
      toast.error(t('miniAssessment.errors.consent'));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mini-assessment`, {
        email: form.email.trim(),
        name: form.name.trim() || null,
        company: form.company.trim() || null,
        answers,
        lang,
        consent: form.consent,
      });
      setResult(res.data);
      setPhase('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      logger.warn('[MiniAssessment] submit failed:', err?.message || err);
      toast.error(err.response?.data?.detail || t('miniAssessment.errors.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRetake = () => {
    setAnswers({});
    setForm({ email: '', name: '', company: '', consent: false });
    setResult(null);
    setStep(0);
    setPhase('questions');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const tier = result?.tier || 'developing';
  const colors = TIER_COLOR[tier];
  const gaps = (result?.areas || []).filter((a) => a.score < 100);
  const strengths = (result?.areas || []).filter((a) => a.score >= 100);

  return (
    <PublicShell testId="mini-assessment-page">
      <main id="main-content" tabIndex={-1} className="site-container public-main focused-form">
      <div>
        {/* Header (hidden on result) */}
        {phase !== 'result' && (
          <>
            <div className="mb-8">
              <div className="data-eyebrow inline-flex items-center gap-2 mb-4">
                <Sparkles className="w-4 h-4" /> {t('miniAssessment.badge')}
              </div>
              <h1 className="text-3xl sm:text-4xl font-semibold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="mini-heading">
                {t('miniAssessment.heading')}
              </h1>
              <p className="mt-3 text-sm text-[#66707A]">
                {t('miniAssessment.subheading')}
              </p>
            </div>
            <div
              className="flex gap-1.5 mb-8"
              role="progressbar"
              aria-valuenow={progressValue}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('miniAssessment.progress', { current: Math.min(step + 1, total), total })}
              data-testid="mini-progress"
            >
              {QUESTION_IDS.map((id, i) => {
                const filled = phase !== 'questions' || i < step;
                const current = phase === 'questions' && i === step;
                let segClass = 'bg-[#DDE1E5]';
                if (filled) segClass = 'bg-[#171A1D]';
                else if (current) segClass = 'bg-[#78B928]';
                return <div key={id} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${segClass}`} />;
              })}
            </div>
          </>
        )}

        {/* QUESTIONS */}
        {phase === 'questions' && (
          <div key={currentId} className="animate-fade-in-up" data-testid="mini-question-card">
            <div className="bg-white rounded-lg border border-[#DDE1E5] p-7 sm:p-8 shadow-card">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#527F19] mb-2" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
              {t('miniAssessment.progress', { current: step + 1, total })}
            </p>
            <h2 className="text-lg sm:text-xl font-bold text-[#171A1D] mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="mini-question-text">
              {t(`miniAssessment.questions.${currentId}`)}
            </h2>
            <div className="space-y-3">
              {OPTIONS.map((opt) => {
                const selected = answers[currentId] === opt.value;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setAnswer(opt.value)}
                    data-testid={`mini-option-${opt.key}`}
                    aria-pressed={selected}
                    className={`group w-full text-left px-5 py-4 rounded-lg border flex items-center gap-3.5 transition-[border-color,background-color,box-shadow] duration-200 ${
                      selected
                        ? 'border-[#527F19] bg-[#F1F8E8] shadow-card'
                        : 'border-[#DDE1E5] bg-white hover:border-[#527F19]/50 hover:bg-[#FAFAFA] hover:shadow-sm'
                    }`}
                  >
                    <span
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors duration-200 ${
                        selected ? 'border-[#527F19] bg-[#527F19]' : 'border-[#DDE1E5] bg-white group-hover:border-[#527F19]/60'
                      }`}
                      aria-hidden="true"
                    >
                      {selected && <span className="w-2 h-2 rounded-full bg-white" />}
                    </span>
                    <span className={`text-sm font-medium flex-1 ${selected ? 'text-[#171A1D]' : 'text-[#3A4249]'}`}>
                      {t(`miniAssessment.options.${opt.key}`)}
                    </span>
                    {selected && <CheckCircle2 className="w-5 h-5 text-[#527F19] shrink-0" />}
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={goBack}
                disabled={step === 0}
                data-testid="mini-back-btn"
                className="text-[#66707A]"
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('miniAssessment.back')}
              </Button>
              <Button
                onClick={goNext}
                disabled={!answered}
                data-testid="mini-next-btn"
                className="bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold px-6 shadow-card"
              >
                {step === total - 1 ? t('miniAssessment.seeScore') : t('miniAssessment.next')}
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#8A929B]">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-[#78B928]" /> {t('landing.freeToUse')}</span>
              <span className="flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#527F19]" /> {t('landing.noCreditCard')}</span>
              <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5 text-[#527F19]" /> {t('landing.instantResults')}</span>
            </div>
          </div>
        )}

        {/* EMAIL GATE */}
        {phase === 'gate' && (
          <form onSubmit={handleReveal} data-testid="mini-gate-form" className="bg-white rounded-lg border border-[#DDE1E5] p-7 shadow-sm">
            <div className="flex items-center gap-2 text-[#527F19] mb-1">
              <Lock className="w-5 h-5" />
              <span className="text-xs font-semibold uppercase tracking-wider">{t('miniAssessment.gate.tag')}</span>
            </div>
            <h2 className="text-xl font-bold text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {t('miniAssessment.gate.title')}
            </h2>
            <p className="mt-2 text-sm text-[#66707A]">{t('miniAssessment.gate.subtitle')}</p>

            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="mini-email" className="text-sm font-medium text-[#171A1D]">{t('miniAssessment.gate.email')} *</Label>
                <Input
                  id="mini-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t('miniAssessment.gate.emailPh')}
                  data-testid="mini-email-input"
                  className="mt-1.5"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="mini-name" className="text-sm font-medium text-[#171A1D]">{t('miniAssessment.gate.name')}</Label>
                  <Input
                    id="mini-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t('miniAssessment.gate.namePh')}
                    data-testid="mini-name-input"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="mini-company" className="text-sm font-medium text-[#171A1D]">{t('miniAssessment.gate.company')}</Label>
                  <Input
                    id="mini-company"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    placeholder={t('miniAssessment.gate.companyPh')}
                    data-testid="mini-company-input"
                    className="mt-1.5"
                  />
                </div>
              </div>
              <div className="flex items-start gap-2.5 pt-1">
                <Checkbox
                  id="mini-consent"
                  checked={form.consent}
                  onCheckedChange={(v) => setForm({ ...form, consent: v === true })}
                  data-testid="mini-consent-checkbox"
                  className="mt-0.5"
                />
                <Label htmlFor="mini-consent" className="text-xs text-[#66707A] leading-relaxed font-normal cursor-pointer">
                  {t('miniAssessment.gate.consent')}{' '}
                  <LocaleLink to="/privacy" className="text-[#527F19] hover:underline">{t('miniAssessment.gate.privacyLink')}</LocaleLink>.
                </Label>
              </div>
            </div>

            <div className="mt-7 flex items-center justify-between">
              <Button type="button" variant="ghost" onClick={goBack} data-testid="mini-gate-back-btn" className="text-[#66707A]">
                <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('miniAssessment.back')}
              </Button>
              <Button type="submit" disabled={loading} data-testid="mini-reveal-btn" className="bg-[#527F19] hover:bg-[#3F6114] text-white font-semibold px-6">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('miniAssessment.gate.submitting')}</> : <>{t('miniAssessment.gate.reveal')} <ArrowRight className="w-4 h-4 ml-1.5" /></>}
              </Button>
            </div>
          </form>
        )}

        {/* RESULT */}
        {phase === 'result' && result && (
          <div data-testid="mini-result">
            <div className={`rounded-lg border ${colors.border} ${colors.bg} p-8 text-center`}>
              <div className="flex justify-center">
                <ScoreRing score={result.overall_score} color={colors.ring} />
              </div>
              <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#8A929B]">{t('miniAssessment.result.scoreLabel')}</p>
              <h2 className={`mt-1 text-2xl font-semibold ${colors.text}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="mini-tier-label">
                {t(`miniAssessment.result.tiers.${tier}.label`)}
              </h2>
              <p className="mt-3 text-sm text-[#3A4249] max-w-md mx-auto leading-relaxed" data-testid="mini-tier-summary">
                {t(`miniAssessment.result.tiers.${tier}.summary`)}
              </p>
            </div>

            {/* Area breakdown */}
            <div className="mt-6 bg-white rounded-lg border border-[#DDE1E5] p-6">
              <h3 className="text-sm font-bold text-[#171A1D] mb-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('miniAssessment.result.breakdownTitle')}
              </h3>
              <div className="space-y-3">
                {(result.areas || []).map((a) => {
                  let barColor = '#B42318';
                  if (a.score >= 100) barColor = '#78B928';
                  else if (a.score >= 50) barColor = '#B7791F';
                  return (
                    <div key={a.id} data-testid={`mini-area-${a.id}`}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="text-[#3A4249] font-medium">{t(`miniAssessment.areas.${a.id}`)}</span>
                        <span className="text-xs text-[#8A929B] font-mono">{a.score}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#F5F6F7] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${a.score}%`, backgroundColor: barColor, transition: 'width 0.8s ease-out' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
              {gaps.length > 0 && (
                <p className="mt-4 text-xs text-[#66707A]">
                  {t('miniAssessment.result.gapsNote', { count: gaps.length })}
                </p>
              )}
              {gaps.length === 0 && strengths.length > 0 && (
                <p className="mt-4 text-xs text-[#527F19]">{t('miniAssessment.result.allStrong')}</p>
              )}
            </div>

            {/* CTAs */}
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <LocaleLink to="/calculator" className="group">
                <div className="h-full rounded-lg border border-[#DDE1E5] bg-white p-6 hover:border-[#527F19]/40 hover:shadow-raised transition-all" data-testid="mini-cta-calculator">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-[#527F19] transition-colors">
                    <Calculator className="w-5 h-5 text-[#527F19] group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="mt-3 font-bold text-base text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {t('miniAssessment.result.ctaCalcTitle')}
                  </h4>
                  <p className="mt-1 text-sm text-[#66707A]">{t('miniAssessment.result.ctaCalcDesc')}</p>
                  <span className="mt-3 inline-flex items-center text-sm font-semibold text-[#527F19]">
                    {t('homepage.primaryAction')} <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </LocaleLink>
              <LocaleLink to="/assessment" className="group">
                <div className="h-full rounded-lg border border-[#DDE1E5] bg-white p-6 hover:border-[#527F19]/40 hover:shadow-raised transition-all" data-testid="mini-cta-assessment">
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center group-hover:bg-[#527F19] transition-colors">
                    <BrainCircuit className="w-5 h-5 text-[#527F19] group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="mt-3 font-bold text-base text-[#171A1D]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {t('miniAssessment.result.ctaAssessTitle')}
                  </h4>
                  <p className="mt-1 text-sm text-[#66707A]">{t('miniAssessment.result.ctaAssessDesc')}</p>
                  <span className="mt-3 inline-flex items-center text-sm font-semibold text-[#527F19]">
                    {t('miniAssessment.result.ctaAssessBtn')} <ArrowRight className="w-4 h-4 ml-1" />
                  </span>
                </div>
              </LocaleLink>
            </div>

            <div className="mt-6 text-center">
              <button onClick={handleRetake} data-testid="mini-retake-btn" className="text-sm text-[#66707A] hover:text-[#527F19] transition-colors inline-flex items-center gap-1.5">
                <Shield className="w-4 h-4" /> {t('miniAssessment.result.retake')}
              </button>
            </div>
          </div>
        )}
      </div>
      </main>
    </PublicShell>
  );
}
